import { useEffect, useState } from "react";
import { StockPile } from "./StockPile.js";
import { WastePile } from "./WastePile.js";
import { FoundationPile } from "./FoundationPile.js";
import { TableauColumn } from "./TableauColumn.js";
import { useCardSet } from "../contexts/CardSetContext.js";
import { FlyAnimationProvider } from "../contexts/FlyAnimationContext.js";
import { GAME_CONFIG } from "../config.js";
import { useI18n } from "../i18n/index.js";
import { useGameStore } from "../store/useGameStore.js";
import { DebugCardPanel } from "./DebugCardPanel.js";

// ============================================================
// GameBoard — Gesamtes Spielfeld
//
// Layout (7 Spalten):
//   Normal:      [Stock] [Waste] [leer] [F1] [F2] [F3] [F4]
//   Left-handed: [F1] [F2] [F3] [F4] [leer] [Waste] [Stock]
//   Tableau:     [Col-0] [Col-1] [Col-2] [Col-3] [Col-4] [Col-5] [Col-6]
// ============================================================

export function GameBoard() {
  const { leftHanded } = useCardSet();
  const { t } = useI18n();

  // Loading-Overlay: verhindert den kurzen Flash von unfertig gerendertem Board.
  // Nur beim ersten Mount nach newGame zeigen (nicht nach Leaderboard-Rückkehr).
  const splashDone = useGameStore((s) => s._splashDone);
  const [phase, setPhase] = useState<"visible" | "fading" | "done">(splashDone ? "done" : "visible");

  useEffect(() => {
    if (phase === "done") return;
    // 2 Sekunden sichtbar, dann 400ms ausfaden
    const showTimer = setTimeout(() => setPhase("fading"), 2000);
    return () => clearTimeout(showTimer);
  }, [phase]);

  useEffect(() => {
    if (phase !== "fading") return;
    const fadeTimer = setTimeout(() => setPhase("done"), 400);
    return () => clearTimeout(fadeTimer);
  }, [phase]);

  // Splash als gezeigt markieren wenn er fertig ist
  useEffect(() => {
    if (phase === "done" && !splashDone) {
      useGameStore.setState({ _splashDone: true });
    }
  }, [phase, splashDone]);

  return (
    <FlyAnimationProvider>
    <div className="w-full max-w-2xl mx-auto px-2 sm:px-4 py-2">
      {/* Obere Reihe */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 sm:mb-3">
        {leftHanded ? (
          <>
            <FoundationPile foundationIndex={0} />
            <FoundationPile foundationIndex={1} />
            <FoundationPile foundationIndex={2} />
            <FoundationPile foundationIndex={3} />
            {/* Lücke */}
            <div />
            <WastePile />
            <StockPile />
          </>
        ) : (
          <>
            <StockPile />
            <WastePile />
            {/* Lücke */}
            <div />
            <FoundationPile foundationIndex={0} />
            <FoundationPile foundationIndex={1} />
            <FoundationPile foundationIndex={2} />
            <FoundationPile foundationIndex={3} />
          </>
        )}
      </div>

      {/* Tableau */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 items-start">
        {Array.from({ length: 7 }, (_, i) => (
          <TableauColumn key={i} columnIndex={i} />
        ))}
      </div>
      {/* Loading-Overlay: fullscreen, verdeckt Board-Flash beim Laden */}
      {phase !== "done" && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: GAME_CONFIG.loadingScreenColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "opacity 400ms ease-out",
            opacity: phase === "fading" ? 0 : 1,
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <img
              src="/images/loading-logo.jpg"
              alt=""
              style={{ maxWidth: "50%", maxHeight: "40%", objectFit: "contain", margin: "0 auto" }}
            />
            <p style={{ color: "white", fontSize: "1.25rem", marginTop: "1.5rem", opacity: 0.8 }}>
              {t("game.shuffling")}
            </p>
          </div>
        </div>
      )}
      {/* Debug-Panel: Karten-Status-Debugger (unten fixiert, mobil-tauglich) */}
      <DebugCardPanel />
    </div>
    </FlyAnimationProvider>
  );
}
